import { apiEndpoints } from '@config/env';
import { postData, getData, deleteData } from './global';
import { debug, error as logError, warn } from './console';
import type { AssistantAskContext } from '../store/assistantContextStore';

export type AssistantProgressEvent = {
  event: string;
  data: unknown;
};

export type AssistantAnswer = {
  answer: string;
  conversation_id: string;
  message_id: string;
  tools_used?: string[];
  ui_actions?: Array<{
    type: string;
    domain?: string;
    event_ids?: number[];
    label?: string;
  }>;
  cost?: { model: string; usd: number; usage?: unknown } | null;
  memory_used?: boolean;
  clarify?: { question: string; options: string[] } | null;
};

export type AskStreamHandlers = {
  onProgress?: (event: string, data: unknown) => void;
  onAnswer?: (answer: AssistantAnswer) => void;
  onError?: (message: string) => void;
};

function projectQuery(projectId: number): string {
  return `project_id=${encodeURIComponent(String(projectId))}`;
}

/** Parse SSE frames from a POST body reader. */
export async function askAssistantStream(
  body: {
    question: string;
    conversation_id?: string | null;
    page_name?: string | null;
    menu_name?: string | null;
    project_id: number;
    context?: AssistantAskContext | null;
    ask_id?: string;
  },
  handlers: AskStreamHandlers,
  signal?: AbortSignal
): Promise<AssistantAnswer | null> {
  const url = apiEndpoints.assistant.askStream;
  const { authManager } = await import('./authManager');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const token = authManager.getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const csrf = typeof document !== 'undefined'
    ? document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1]
    : null;
  if (csrf) headers['X-CSRF-Token'] = decodeURIComponent(csrf);

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    const msg = `Ask failed (${res.status}): ${text.slice(0, 200)}`;
    handlers.onError?.(msg);
    throw new Error(msg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalAnswer: AssistantAnswer | null = null;
  let currentEvent = 'message';

  const flushFrame = (raw: string) => {
    const lines = raw.split('\n');
    let event = currentEvent;
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      else if (line.startsWith(':')) {
        /* keepalive */
      }
    }
    if (!dataLines.length) return;
    let data: unknown = dataLines.join('\n');
    try {
      data = JSON.parse(dataLines.join('\n'));
    } catch {
      /* keep string */
    }
    handlers.onProgress?.(event, data);
    if (event === 'answer') {
      finalAnswer = data as AssistantAnswer;
      handlers.onAnswer?.(finalAnswer);
    }
    if (event === 'error') {
      const msg =
        data && typeof data === 'object' && 'message' in (data as object)
          ? String((data as { message: string }).message)
          : 'Assistant error';
      handlers.onError?.(msg);
    }
    currentEvent = 'message';
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      if (frame.trim()) flushFrame(frame);
    }
  }
  if (buffer.trim()) flushFrame(buffer);
  return finalAnswer;
}

export async function cancelAsk(askId: string, projectId: number): Promise<void> {
  try {
    await postData(apiEndpoints.assistant.askCancel, { ask_id: askId, project_id: projectId });
  } catch (err) {
    warn('[Capitan] cancel failed', (err as Error)?.message);
  }
}

export async function listConversations(projectId: number, limit = 30): Promise<unknown[]> {
  const res = await getData(`${apiEndpoints.assistant.conversations}?${projectQuery(projectId)}&limit=${limit}`);
  if (!res?.success) return [];
  return (res.data as unknown[]) || [];
}

export async function loadConversation(
  projectId: number,
  conversationId: string
): Promise<{ conversation: unknown; messages: unknown[] } | null> {
  const res = await getData(
    `${apiEndpoints.assistant.conversations}/${conversationId}?${projectQuery(projectId)}`
  );
  if (!res?.success) return null;
  return res.data as { conversation: unknown; messages: unknown[] };
}

export async function deleteConversation(projectId: number, conversationId: string): Promise<boolean> {
  const res = await deleteData(
    `${apiEndpoints.assistant.conversations}/${conversationId}?${projectQuery(projectId)}`,
    { project_id: projectId }
  );
  return !!res?.success;
}

export async function sendFeedback(opts: {
  projectId: number;
  messageId: string;
  rating: 'up' | 'down';
  comment?: string;
}): Promise<boolean> {
  const res = await postData(apiEndpoints.assistant.feedback, {
    project_id: opts.projectId,
    message_id: opts.messageId,
    rating: opts.rating,
    comment: opts.comment,
  });
  return !!res?.success;
}

export async function rememberFinding(opts: {
  projectId: number;
  messageId: string;
  claim?: string;
}): Promise<boolean> {
  const res = await postData(apiEndpoints.assistant.memoryRemember, {
    project_id: opts.projectId,
    message_id: opts.messageId,
    claim: opts.claim,
  });
  return !!res?.success;
}

export { debug, logError };
